import type { OrderTypeEnum, PositionSide } from '@kwenta/sdk/types'
import { type ChangeEvent, useMemo } from 'react'
import styled from 'styled-components'

import { formatDollars } from '@kwenta/sdk/utils'
import Slider from '@mui/material/Slider'
import InputHeaderRow from 'components/Input/InputHeaderRow'
import InputTitle from 'components/Input/InputTitle'
import NumericInput from 'components/Input/NumericInput'
import { Body } from 'components/Text'
import { FlexDivRow } from 'components/layout/flex'
import { FlexDivRowCentered } from 'components/layout/flex'
import { useTranslation } from 'react-i18next'
import { orderPriceInvalidLabel } from 'utils/futures'

type Props = {
	orderPrice: string
	marketPrice: string
	orderType: OrderTypeEnum
	positionSide: PositionSide
	onChange: (e: ChangeEvent<HTMLInputElement>, v: string) => void
	showSlider?: boolean
	stopLossPrice?: string
	takeProfitPrice?: string
}

export default function OrderPriceInput({
	orderPrice,
	orderType,
	positionSide,
	marketPrice,
	onChange,
	stopLossPrice,
	takeProfitPrice,
	showSlider = false,
}: Props) {
	const { t } = useTranslation()
	const minMaxLabelString = useMemo(
		() => orderPriceInvalidLabel(orderPrice, positionSide, marketPrice, orderType),
		[orderPrice, orderType, positionSide, marketPrice]
	)

	return (
		<div>
			<InputHeaderRow
				label={
					<StyledInputTitle>
						{orderType} Price{' '}
						{minMaxLabelString && (
							<>
								&nbsp; —<span>&nbsp; {minMaxLabelString}</span>
							</>
						)}
					</StyledInputTitle>
				}
			/>
			<NumericInput
				invalid={!!minMaxLabelString}
				dataTestId="order-price-input"
				value={orderPrice}
				placeholder="0.0"
				onChange={onChange}
				right={<Body color="secondary">USD</Body>}
			/>
			{showSlider && stopLossPrice && takeProfitPrice && (
				<>
					<SliderContainer>
						<SliderEnd side="left" />
						<StyledSlider
							min={Number(stopLossPrice)}
							max={Number(takeProfitPrice)}
							value={Number(orderPrice)}
							onChange={(_, value: number | number[]) => {
								if (typeof value === 'number') {
									const syntheticEvent = {
										target: { value: value.toString() },
										currentTarget: { value: value.toString() },
									} as ChangeEvent<HTMLInputElement>
									onChange(syntheticEvent, value.toString())
								}
							}}
						/>
						<SliderEnd side="right" />
					</SliderContainer>
					<FlexDivRowCentered>
						<FlexDivRow columnGap="4px">
							<Body>{formatDollars(stopLossPrice, { suggestDecimals: true })}</Body>
							<Body color="secondary">
								{t('futures.market.trade.edit-conditional-order.stop-loss')}
							</Body>
						</FlexDivRow>
						<FlexDivRow columnGap="4px">
							<Body color="secondary">
								{t('futures.market.trade.edit-conditional-order.take-profit')}
							</Body>
							<Body>{formatDollars(takeProfitPrice, { suggestDecimals: true })}</Body>
						</FlexDivRow>
					</FlexDivRowCentered>
				</>
			)}
		</div>
	)
}

const SliderContainer = styled.div`
	height: 24px;
	padding: 0px 4px 0px 4px;
	box-sizing: border-box;
	position: relative;
`

const SliderEnd = styled.div<{ side: 'left' | 'right' }>`
	z-index: 50;
	position: absolute;
	background-color: ${(props) => props.theme.colors.selectedTheme.slider.rail.background};
	width: 8px;
	height: 8px;
	border-radius: 50%;
	top: 11px;
	${({ side }) => (side === 'left' ? 'left: 0px;' : 'right: 0px;')}
`

const StyledSlider = styled(Slider)`
	.MuiSlider-rail {
		z-index: 10;
		border-radius: 0px;
		background-color: ${(props) => props.theme.colors.selectedTheme.slider.rail.background};
		height: 2px;
		left: 0;
		right: 0;
		opacity: 1;
	}

	.MuiSlider-track {
		height: 2px;
	}

	.MuiSlider-thumb {
		z-index: 51;
		background-color: ${(props) => props.theme.colors.selectedTheme.slider.thumb.background};
		border: ${(props) => props.theme.colors.selectedTheme.slider.thumb.slimBorder};
		width: 8px;
		height: 8px;
	}
`

const StyledInputTitle = styled(InputTitle)`
	text-transform: capitalize;
	span {
		color: ${(props) => props.theme.colors.selectedTheme.red};
	}
`
